import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Sidebar from "./components/Sidebar";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import CoachDashboard from "./pages/CoachDashboard";
import CoachSettings from "./pages/CoachSettings";
import CoachManagement from "./pages/CoachManagement";
import UserManagement from "./pages/UserManagement";
import AssessmentFlow from "./pages/AssessmentFlow";
import ReportPage from "./pages/ReportPage";
import TokenAssessment from "./pages/TokenAssessment";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OrgReports from "./pages/OrgReports";
import AssessmentVersions from "./pages/AssessmentVersions";
import CoachComparison from "./pages/CoachComparison";
import HomePage from "./pages/HomePage";
import { Eye, X } from "lucide-react";
import "./App.css";

function ImpersonationBanner() {
    const { user, stopImpersonation } = useAuth();
    const navigate = useNavigate();
    if (!user?.impersonating) return null;

    const handleStop = async () => {
        await stopImpersonation();
        window.location.href = "/admin";
    };

    return (
        <div data-testid="impersonation-banner" className="fixed top-14 md:top-0 left-0 right-0 z-[90] flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2"
            style={{ backgroundColor: "#F59E0B", color: "#1E293B" }}>
            <Eye className="h-4 w-4 hidden sm:block" />
            <span className="text-xs sm:text-sm font-semibold truncate" style={{ fontFamily: "Outfit" }}>
                Viewing as {user.name} ({user.role === "coach" ? "Coach" : "User"}) — Read-only
            </span>
            <button data-testid="stop-impersonation-btn" onClick={handleStop}
                className="ml-1 sm:ml-2 flex items-center gap-1 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-semibold transition-colors flex-shrink-0"
                style={{ backgroundColor: "#1E293B", color: "#F59E0B" }}>
                <X className="h-3.5 w-3.5" /> Exit
            </button>
        </div>
    );
}

function ProtectedRoute({ allowedRoles }) {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );
    if (!user) return <Navigate to="/login" replace />;
    const effectiveRole = user.role;
    if (allowedRoles && !allowedRoles.includes(effectiveRole)) {
        if (user.impersonating) {
            if (effectiveRole === "coach") return <Navigate to="/coach" replace />;
            if (effectiveRole === "assessment_user") return <Navigate to="/assessment" replace />;
        }
        return <Navigate to="/" replace />;
    }
    return <Outlet />;
}

function DashboardLayout() {
    const { user } = useAuth();
    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <main className="flex-1 overflow-y-auto bg-background pt-14 md:pt-0" style={{ paddingTop: user?.impersonating ? undefined : undefined }}>
                {user?.impersonating && <div className="h-10 md:h-10" />}
                <Outlet />
            </main>
        </div>
    );
}

function AssessmentLayout() {
    const { user } = useAuth();
    return (
        <div>
            {user?.impersonating && <div className="h-10" />}
            <Outlet />
        </div>
    );
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <HomePage />;
    if (user.impersonating) {
        if (user.role === "coach") return <Navigate to="/coach" replace />;
        if (user.role === "assessment_user") return <Navigate to="/assessment" replace />;
    }
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "coach") return <Navigate to="/coach" replace />;
    return <Navigate to="/assessment" replace />;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <ImpersonationBanner />
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                    <Route path="/take/:token" element={<TokenAssessment />} />
                    <Route path="/" element={<RootRedirect />} />

                    {/* Admin routes with sidebar */}
                    <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                        <Route element={<DashboardLayout />}>
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/coaches" element={<CoachManagement />} />
                            <Route path="/admin/users" element={<UserManagement />} />
                            <Route path="/admin/reports/:userId" element={<ReportPage />} />
                            <Route path="/admin/org-reports" element={<OrgReports />} />
                            <Route path="/admin/versions" element={<AssessmentVersions />} />
                            <Route path="/admin/coach-comparison" element={<CoachComparison />} />
                            <Route path="/admin/settings" element={<div className="p-8"><h1 className="text-2xl font-semibold" style={{fontFamily:"Outfit"}}>Settings</h1><p className="text-muted-foreground mt-2">Platform settings coming soon.</p></div>} />
                        </Route>
                    </Route>

                    {/* Coach routes with sidebar */}
                    <Route element={<ProtectedRoute allowedRoles={["coach"]} />}>
                        <Route element={<DashboardLayout />}>
                            <Route path="/coach" element={<CoachDashboard />} />
                            <Route path="/coach/users" element={<UserManagement />} />
                            <Route path="/coach/reports/:userId" element={<ReportPage />} />
                            <Route path="/coach/settings" element={<CoachSettings />} />
                        </Route>
                    </Route>

                    {/* Assessment user - full screen, no sidebar */}
                    <Route element={<ProtectedRoute allowedRoles={["assessment_user"]} />}>
                        <Route element={<AssessmentLayout />}>
                            <Route path="/assessment" element={<AssessmentFlow />} />
                        </Route>
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster richColors position="top-right" />
        </AuthProvider>
    );
}

export default App;
